/**
 * useSession hook — Fetches dual CloudFront signatures from the session API.
 *
 * After Cognito authentication, calls POST /session with the auth token to obtain:
 * - channelUrl: signed URL for channel/* with 72h expiry
 * - vodSignature: separate signature components for vod/* with 5min expiry
 *
 * The VOD signature is refreshed every 2 minutes to stay ahead of its 5-minute expiry.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { playerConfig } from '../config';

/** Interval to refresh VOD signature (2 minutes in ms) */
const VOD_REFRESH_INTERVAL_MS = 2 * 60 * 1000;

interface SessionState {
  /** The signed channel playback URL (full URL with query params) */
  playbackUrl: string;
  /** VOD signature query params string ready to append: Policy=...&Signature=...&Key-Pair-Id=... */
  vodSignatureParams: string;
  /** Live signature query params string ready to append: Policy=...&Signature=...&Key-Pair-Id=... */
  liveSignatureParams: string;
  /** Whether the initial session is being loaded */
  isLoading: boolean;
  /** Error message if session initialization failed */
  error: string | null;
  /** Retry the session initialization */
  retry: () => void;
}

export function useSession(): SessionState {
  const [playbackUrl, setPlaybackUrl] = useState('');
  const [vodSignatureParams, setVodSignatureParams] = useState('');
  const [liveSignatureParams, setLiveSignatureParams] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchSession = useCallback(async (): Promise<boolean> => {
    const sessionApiUrl = playerConfig.sessionApiUrl;

    // If no session API configured, fall back to direct playback URL
    if (!sessionApiUrl) {
      setPlaybackUrl(playerConfig.playbackUrl);
      return true;
    }

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Include auth token if available (dev/staging with Cognito)
      const token = sessionStorage.getItem('capoeira_auth_token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${sessionApiUrl}session`, {
        method: 'POST',
        headers,
      });

      if (response.status === 401) {
        // Token expired or not provided — try without auth
        sessionStorage.removeItem('capoeira_auth_token');
        setError('Session expirée. Veuillez vous reconnecter.');
        return false;
      }

      if (!response.ok) {
        throw new Error(`Session API error: ${response.status}`);
      }

      const data = await response.json();

      if (data.channelUrl) {
        setPlaybackUrl(data.channelUrl);
      }

      if (data.vodSignature) {
        const params = `Policy=${data.vodSignature.Policy}&Signature=${data.vodSignature.Signature}&Key-Pair-Id=${data.vodSignature['Key-Pair-Id']}`;
        setVodSignatureParams(params);
      }

      if (data.liveSignature) {
        const params = `Policy=${data.liveSignature.Policy}&Signature=${data.liveSignature.Signature}&Key-Pair-Id=${data.liveSignature['Key-Pair-Id']}`;
        setLiveSignatureParams(params);
      }

      setError(null);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur réseau';
      setError(`Impossible d'initialiser la session: ${message}`);
      // Fall back to direct playback URL if available
      if (playerConfig.playbackUrl) {
        setPlaybackUrl(playerConfig.playbackUrl);
      }
      return false;
    }
  }, []);

  const initSession = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const success = await fetchSession();

    setIsLoading(false);

    // Set up 2-minute refresh interval for VOD signature
    if (success) {
      // Clear any existing interval
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }

      refreshIntervalRef.current = setInterval(async () => {
        // Silently refresh — don't set loading state
        await fetchSession();
      }, VOD_REFRESH_INTERVAL_MS);
    }
  }, [fetchSession]);

  useEffect(() => {
    initSession();

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [initSession, retryCount]);

  const retry = useCallback(() => {
    setRetryCount((c) => c + 1);
  }, []);

  return { playbackUrl, vodSignatureParams, liveSignatureParams, isLoading, error, retry };
}
